import React from "react";
import { clsx } from "clsx";

type LoadingStateProps = {
  message?: string;
  description?: string;
  variant?: "overlay" | "fullscreen" | "inline";
  className?: string;
};

/**
 * Sleek loading indicator that can be used inline, as an overlay, or fullscreen.
 */
const LoadingState: React.FC<LoadingStateProps> = ({
  message = "Đang tải dữ liệu",
  description = "Hệ thống đang xử lý, vui lòng chờ trong giây lát.",
  variant = "overlay",
  className,
}) => {
  const containerClass = clsx(
    variant === "fullscreen" &&
      "fixed inset-0 z-50 flex items-center justify-center bg-white/80 backdrop-blur",
    variant === "overlay" &&
      "absolute inset-0 z-20 flex items-center justify-center rounded-xl bg-white/75 backdrop-blur-[2px]",
    variant === "inline" && "flex items-center justify-center",
    className,
  );

  return (
    <div className={containerClass} role="status" aria-live="polite">
      <div className="relative overflow-hidden rounded-2xl border border-gray-100 bg-white/95 px-4 py-3 shadow-xl">
        <div className="absolute inset-x-4 top-0 h-px bg-gradient-to-r from-blue-500/0 via-blue-500/60 to-blue-500/0" />
        <div className="flex items-center gap-3">
          <div className="relative h-12 w-12">
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-500 via-sky-400 to-indigo-500 opacity-30 blur-lg" />
            <div className="relative h-full w-full rounded-full border border-blue-100 bg-white shadow-inner" />
            <div className="absolute inset-1 rounded-full border-2 border-transparent border-t-blue-500 border-r-sky-400 animate-spin" />
          </div>
          <div className="space-y-0.5">
            <p className="text-sm font-semibold text-gray-800">{message}</p>
            {description && (
              <p className="text-xs text-gray-500">{description}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoadingState;
